import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Breadcrumbs from '../components/Breadcrumbs';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CategoryPage = () => {
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState('featured');
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoryData, productsData] = await Promise.all([
          api.getCategory(slug),
          api.getProducts({ category: slug, sort: sortBy, limit: 100 })
        ]);
        setCategory(categoryData);
        setProducts(productsData.products);
      } catch (error) {
        console.error('Error fetching data:', error);
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Category Not Found</h1>
          <p className="text-slate-600">The category you&apos;re looking for doesn&apos;t exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-full mx-auto px-8 lg:px-16">
          <Breadcrumbs 
            items={[
              { label: 'Shop', href: '/shop' },
              { label: category.name }
            ]} 
          />
        </div>
      </div>

      {/* Category Hero — uses brand banner when available, falls back to gradient */}
      <section
        className="relative py-20 sm:py-28 overflow-hidden bg-gradient-to-br from-teal-50 to-blue-50"
        data-testid="category-hero"
      >
        {category.banner_image && (
          <>
            <img
              src={category.banner_image}
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Brand-tinted gradient overlay for legibility & mood */}
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15,138,122,0.18) 0%, rgba(247,244,235,0.55) 60%, rgba(247,244,235,0.92) 100%)',
              }}
            />
          </>
        )}
        <div className="relative max-w-full mx-auto px-8 lg:px-16">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 tracking-tight drop-shadow-sm">
              {category.name}
            </h1>
            <p className="text-base sm:text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
              {category.description}
            </p>
            <div className="mt-6">
              <span className="inline-block bg-white/95 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium text-slate-700 shadow-sm">
                {products.length} Products Available
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-full mx-auto px-8 lg:px-16">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">All Products</h2>
              <p className="text-slate-600 mt-1">{products.length} results</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Rating</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products */}
          {products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No products found in this category.</p>
            </div>
          ) : (
            <div className="product-grid-modern">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;